import { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { areFiltersEqual } from "./comparator";
import type {
  PageFilters,
  PageFiltersContextType,
  PageFiltersProps,
} from "./types";

/**
 * React context for managing URL-based page filters.
 * Provides methods to sync filter state with URL search parameters.
 */
export const PageFiltersContext = createContext<PageFiltersContextType>(
  {} as PageFiltersContextType,
);

/**
 * Hook to access page filters context with type safety.
 */
export const usePageFilters = <T extends PageFilters>() => {
  return useContext(PageFiltersContext) as unknown as PageFiltersContextType<T>;
};

const isInvalidValue = (value: unknown): boolean => {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "number" && isNaN(value))
  );
};

export const PageFiltersContextProvider = (props: PageFiltersProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getFilter = useCallback(
    (key: string): string => {
      return searchParams.get(key) || "";
    },
    [searchParams],
  );

  const setFilter = useCallback(
    (key: string, value: string | undefined): void => {
      const currentValue = getFilter(key);

      if (isInvalidValue(value)) {
        if (currentValue === "") {
          return;
        }
        const newParams = new URLSearchParams(searchParams);
        newParams.delete(key);
        setSearchParams(newParams);
      } else {
        if (currentValue === String(value)) {
          return;
        }
        const newParams = new URLSearchParams(searchParams);
        newParams.set(key, String(value));
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams, getFilter],
  );

  const setFilters = useCallback(
    (filters: Record<string, string | undefined>) => {
      const newParams = new URLSearchParams(searchParams);
      let hasChanges = false;

      for (const [key, value] of Object.entries(filters)) {
        const currentValue = getFilter(key);

        if (isInvalidValue(value)) {
          if (currentValue !== "") {
            newParams.delete(key);
            hasChanges = true;
          }
        } else {
          if (currentValue !== String(value)) {
            newParams.set(key, String(value));
            hasChanges = true;
          }
        }
      }

      if (hasChanges) {
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams, getFilter],
  );

  const handleChangeFilter = useCallback(
    (key: string) => {
      return (value: string | undefined) => {
        setFilter(key, value);
      };
    },
    [setFilter],
  );

  const removeFilter = useCallback(
    (key: string) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete(key);
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const removeFilters = useCallback(
    (keys: string[]) => {
      const newParams = new URLSearchParams(searchParams);

      for (const key of keys) {
        newParams.delete(key);
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const initPageFilters = useCallback(
    (defaultFilters: PageFilters, update?: boolean): void => {
      const newParams = new URLSearchParams(searchParams);

      let hasChanges = false;

      Object.entries(defaultFilters).forEach(([key, value]) => {
        if ((update || !searchParams.has(key)) && !isInvalidValue(value)) {
          newParams.set(key, String(value));
          hasChanges = true;
        }
      });

      if (hasChanges) {
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams],
  );

  const prevFiltersRef = useRef<PageFilters>(props.defaultFilters || {});

  const filters = useMemo(() => {
    const newFilters = Object.fromEntries(searchParams.entries());
    const result = { ...(props.defaultFilters || {}), ...newFilters };

    if (areFiltersEqual(prevFiltersRef.current, result)) {
      return prevFiltersRef.current;
    }

    prevFiltersRef.current = result;
    return result;
  }, [searchParams, props.defaultFilters]);

  const context = useMemo((): PageFiltersContextType => {
    return {
      getFilter,
      setFilter,
      setFilters,
      removeFilter,
      removeFilters,
      clearFilters,
      handleChangeFilter,
      initPageFilters,
      filters,
    };
  }, [
    clearFilters,
    getFilter,
    handleChangeFilter,
    initPageFilters,
    removeFilter,
    removeFilters,
    setFilter,
    setFilters,
    filters,
  ]);

  return (
    <PageFiltersContext.Provider value={context}>
      {props.children}
    </PageFiltersContext.Provider>
  );
};
